import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { QuoteRepository, RoutesRepository, VoyagesRepository } from '@data-layer/repositories';
import {
  LclPortModel,
  PackageModel,
  PackageUpdateModel,
  QuoteManualModel,
  QuoteUpdateModel,
  RouteModel,
  VoyageModel,
} from '@data-layer/models/lcl';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { PaginatedResultModel } from '@data-layer/models/common';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { DateHelper } from '@helpers/date.helper';
import { CompanyModel } from '@data-layer/models/companies';
import { combineLatest, Observable, ReplaySubject, Subscriber } from 'rxjs';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { LclContainerService } from '@shared/lcl-mutual/services';
import { LclCreateQuotePackagesComponent } from '../lcl-create-quote/lcl-create-quote-packages/lcl-create-quote-packages.component';
import { enumShippingRole } from '@data-layer/enums/user';
import { enumVoyageStatus } from '@data-layer/enums/lcl';
import { UtilsHelper } from '@helpers/utils-helper';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { UserModel } from '@data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { GenericService } from '@services/generic.service';
import { environment } from '@env/environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { emailPattern } from '@helpers/custom-validator';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import {
  CreateExistingUserManualQuoteError,
  CreateExternalManualQuoteError,
  CreateExternalManualQuoteSuccess,
  CreateManualQuoteSuccess,
} from '@app/modules/lcl-shipping/state';
import { enumLengthUnit, enumRespErrors, enumWeightUnit } from '@data-layer/enums/common';

@Component({
  selector: 'app-lcl-create-quote-external',
  templateUrl: './lcl-create-quote-external.component.html',
  styleUrls: ['./lcl-create-quote-external.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclCreateQuoteExternalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public isHideLCLFooter: boolean;
  @Output() public createQuote: EventEmitter<QuoteUpdateModel> = new EventEmitter<QuoteUpdateModel>();
  @Output() public createExternalManualQuote: EventEmitter<{ quote: QuoteManualModel; recaptchaToken: string }> = new EventEmitter<{
    quote: QuoteManualModel;
    recaptchaToken: string;
  }>();
  @Output() public createManualQuote: EventEmitter<{
    quote: QuoteManualModel;
    email: string;
    password: string;
    formGroup: UntypedFormGroup;
  }> = new EventEmitter<{
    quote: QuoteManualModel;
    email: string;
    password: string;
    formGroup: UntypedFormGroup;
  }>();
  @Output() public getRoutes: EventEmitter<{ fromId: number; toId: number }> = new EventEmitter<{ fromId: number; toId: number }>();

  public formGroup: UntypedFormGroup;
  public packagesFormArray: UntypedFormArray;
  public offerFormGroup: UntypedFormGroup;
  public isSubmitted: boolean;
  public possibleRoute: RouteModel;

  public departurePortFc: UntypedFormControl;
  public arrivalPortFc: UntypedFormControl;
  public emailFc: AbstractControl;
  public nameFc: AbstractControl;
  public passwordFc: AbstractControl;
  public customerNotesFc: AbstractControl;

  public errors: IErrorForm = {
    required: 'LABELS.REQUIRED',
    invalid: 'FORM_ERROR.GA_UNKNOWN',
    accountLocked: 'FORM_ERROR.LOCKED_ACCOUNT_SHORT',
    invalidUsername: 'FORM_ERROR.INVALID_CREDENTIALS',
    invalidPwd: 'FORM_ERROR.INVALID_CREDENTIALS',
    emailNotConfirmed: 'FORM_ERROR.EMAIL_IS_NOT_CONFIRM',
    accountBlocked: 'FORM_ERROR.ACCOUNT_BLOCKED',
    invalidEmail: 'YOU_ENTERED_INVALID_EMAIL',
  };

  @ViewChild('packagesView', { static: false })
  public packagesView: LclCreateQuotePackagesComponent;
  @ViewChild('elementView', { static: false }) public elementView: ElementRef;
  @ViewChild('captchaRef', { static: false }) public captchaRef: RecaptchaComponent;

  public enumShippingRole: typeof enumShippingRole = enumShippingRole;

  public totalValue: number = 0;
  public totalWeight: number = 0;
  public totalUnits: number = 0;
  public isAddingPackages: boolean;

  public isFormEdited: boolean;
  public recalcForm: boolean = true;

  public siteKey: string = environment.reCaptchaV2.siteKey;

  private fromPort: LclPortModel;
  private toPort: LclPortModel;
  private isNotFoundRoutes: boolean;
  private isNotFoundRoutesWarningOpened: boolean;
  private isShowSuccessSendNotification: boolean;
  private isShowUserExistsNotification: boolean;

  private heightSendTrigger$: ReplaySubject<void> = new ReplaySubject<void>();
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private routesRepository: RoutesRepository,
    private voyagesRepository: VoyagesRepository,
    private quoteRepository: QuoteRepository,
    private fh: FormHelper,
    private lclContainerService: LclContainerService,
    private cdr: ChangeDetectorRef,
    private ga: GoogleAnalyticsService,
    private companiesService: CompaniesService,
    private genericService: GenericService,
    private actions: Actions,
  ) {}

  @HostListener('window:resize', ['$event']) public onResize(): void {
    this.heightSendTrigger$.next();
  }

  public ngOnInit(): void {
    this.genericService.isMarketingQuote = true;

    this.formGroup = this.fh.createQuoteFormGroup(false);
    this.departurePortFc = this.formGroup.get(['shippingFrom']) as UntypedFormControl;
    this.departurePortFc.setValidators([Validators.required]);
    this.arrivalPortFc = this.formGroup.get(['shippingTo']) as UntypedFormControl;
    this.arrivalPortFc.setValidators([Validators.required]);
    this.formGroup.get('incoTermsType').setValue(enumIncotermsType.FCA);
    this.offerFormGroup = new UntypedFormGroup({
      email: new UntypedFormControl(null, [Validators.pattern(emailPattern), Validators.required]),
      name: new UntypedFormControl(null, [Validators.required]),
      password: new UntypedFormControl(null),
      customerNotes: new UntypedFormControl(null),
    });
    this.emailFc = this.offerFormGroup.get('email');
    this.nameFc = this.offerFormGroup.get('name');
    this.passwordFc = this.offerFormGroup.get('password');
    this.customerNotesFc = this.offerFormGroup.get('customerNotes');

    this.packagesFormArray = this.formGroup.get('packages') as UntypedFormArray;
    this.isFormEdited = this.checkIsFormEdited;

    this.formGroup.get('shippingRole').setValue(this.company?.shippingRole || enumShippingRole.OTHER);

    combineLatest([this.departurePortFc.valueChanges, this.arrivalPortFc.valueChanges])
      .pipe(
        filter(([fromPortId, toPortId]: [number, number]) => !!fromPortId && !!toPortId),
        takeUntil(this.destroy$),
      )
      .subscribe(([fromId, toId]: [number, number]) => {
        this.getPossibleRoutes(fromId, toId);
      });

    this.calcTotals();

    this.packagesFormArray.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.calcTotals();
      this.cdr.markForCheck();
    });

    this.emailFc.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.emailFc.setErrors(null, { emitEvent: false });
    });

    this.passwordFc.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.passwordFc.setErrors(null, { emitEvent: false });
    });

    this.actions.pipe(ofActionSuccessful(CreateExternalManualQuoteSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.isShowSuccessSendNotification = true;
      this.isNotFoundRoutesWarningOpened = false;
      this.isShowUserExistsNotification = false;
      this.isSubmitted = false;
      this.setIsAddingPackages(false);
      this.formGroup.reset({ incoTermsType: enumIncotermsType.FCA });
      this.emailFc.reset();
      this.nameFc.reset();
      this.customerNotesFc.reset();
      this.emailFc.enable();
      this.nameFc.enable();
      this.customerNotesFc.enable();
      this.packagesFormArray.clear();
      this.packagesFormArray.push(this.fh.createPackageFormGroup(enumLengthUnit.CENTIMETER, enumWeightUnit.KG));
    });

    this.actions
      .pipe(ofActionSuccessful(CreateExternalManualQuoteError), takeUntil(this.destroy$))
      .subscribe((data: CreateExternalManualQuoteError) => {
        this.emailFc.enable();
        this.nameFc.enable();
        this.customerNotesFc.enable();

        this.setFormErrors(data);

        this.cdr.markForCheck();
      });

    this.actions
      .pipe(ofActionSuccessful(CreateExistingUserManualQuoteError), takeUntil(this.destroy$))
      .subscribe((data: CreateExistingUserManualQuoteError) => {
        this.emailFc.enable();
        this.passwordFc.enable();
        this.customerNotesFc.enable();

        this.setFormErrors(data);

        this.cdr.markForCheck();
      });

    this.actions.pipe(ofActionSuccessful(CreateManualQuoteSuccess), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => {
        // added timeout to skip onbeforeunload confirm dialog
        window.top.location.href = `/lcl-shipping?showQuotes=true&showManualQuoteCreated=true`;
      });
    });
  }

  public ngAfterViewInit(): void {
    this.heightSendTrigger$.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe(() => this.sendHeight());
    this.heightSendTrigger$.next();
    this.setupHeightMutationObserver();
  }

  public ngOnDestroy(): void {
    this.genericService.isMarketingQuote = false;

    this.destroy$.next();
  }

  public addPackage(): void {
    if (this.packagesFormArray.valid) {
      const firstPackage: PackageModel = this.packagesFormArray.getRawValue()[0];
      this.packagesFormArray.push(this.fh.createPackageFormGroup(firstPackage?.lengthUnit, firstPackage?.weightUnit));
    } else {
      this.fh.markAsTouched(this.formGroup);
      this.packagesView.forceUpdateForm();
      this.cdr.markForCheck();
    }
  }

  public confirmPackage(): void {
    if (this.packagesFormArray.valid) {
      this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
    } else {
      this.fh.markAsTouched(this.formGroup);
      this.packagesView.forceUpdateForm();
    }
    this.cdr.markForCheck();
  }

  public addLoadDetails(): void {
    this.recalcForm = false;

    if (!this.isAddingPackages) {
      this.onSubmit();
    } else {
      this.addPackage();
      this.packagesView.forceUpdateForm();
    }

    // on mobile it's stuck: i prefer this rather than removing changeDetection from child components
    setTimeout(() => {
      this.recalcForm = true;
      this.cdr.detectChanges();
    });
  }

  public onSubmit(): void {
    if (!this.isAddingPackages) {
      this.setIsAddingPackages(true);

      if (!this.isSubmitted) {
        return;
      }
    }

    this.isSubmitted = true;

    if (this.formGroup.invalid) {
      this.fh.markAsTouched(this.formGroup);
      this.packagesView.forceUpdateForm();
      this.cdr.markForCheck();
      return;
    }

    this.isNotFoundRoutesWarningOpened = true;
    this.saveQuote();
  }

  public onNotesValidated(recaptchaToken?: string): void {
    if (!recaptchaToken) {
      return;
    }

    this.emailFc.disable();
    this.nameFc.disable();
    this.passwordFc.disable();
    this.customerNotesFc.disable();

    const packages: PackageUpdateModel[] = this.lclContainerService.convertPackagesDataForCreateSending(
      this.packagesFormArray.value || [],
      true,
    );

    if (this.isShowUserExistsNotification) {
      this.createManualQuote.emit({
        quote: {
          departurePortId: this.departurePortFc.value,
          arrivalPortId: this.arrivalPortFc.value,
          customerNotes: this.customerNotesFc.value,
          packages,
        },
        email: this.emailFc.value,
        password: this.passwordFc.value,
        formGroup: this.offerFormGroup,
      });
    } else {
      this.createExternalManualQuote.emit({
        quote: {
          departurePortId: this.departurePortFc.value,
          arrivalPortId: this.arrivalPortFc.value,
          customerNotes: this.customerNotesFc.value,
          notRegisteredUserEmail: this.emailFc.value,
          notRegisteredUserName: this.nameFc.value,
          packages,
        },
        recaptchaToken,
      });
    }
  }

  public onNotesSubmit(): void {
    if (
      this.formGroup.invalid ||
      !this.packagesFormArray.controls.length ||
      this.emailFc.invalid ||
      (this.nameFc.enabled && this.nameFc.invalid) ||
      (this.passwordFc.enabled && this.passwordFc.invalid)
    ) {
      if (!this.packagesFormArray.controls.length) {
        this.addPackage();
      }
      this.markFormValidity();
      return;
    }
    this.captchaRef.reset();
    this.captchaRef.execute();
  }

  public removeQuote(index: number): void {
    this.packagesFormArray.removeAt(index);
  }

  public onSelectFromPort(port: LclPortModel): void {
    this.fromPort = port;
  }

  public onSelectToPort(port: LclPortModel): void {
    this.toPort = port;
  }

  public onCloseRouteWarning(): void {
    this.isNotFoundRoutesWarningOpened = false;
  }

  public onCloseSendNotification(): void {
    this.isShowSuccessSendNotification = false;
  }

  public setIsAddingPackages(val: boolean): void {
    this.isAddingPackages = val;
  }

  public onStartSearch(): void {
    this.isNotFoundRoutes = false;
    this.isNotFoundRoutesWarningOpened = false;
  }

  public get isShowRouteWarning(): boolean {
    return this.isNotFoundRoutes && this.isNotFoundRoutesWarningOpened;
  }

  public get isShowQuoteSubmitNotification(): boolean {
    return this.isShowSuccessSendNotification;
  }

  public get isShowLoadTotals(): boolean {
    return this.totalWeight > 0 && this.totalValue >= 0;
  }

  public get isEmptyPackages(): boolean {
    return (
      !this.packagesFormArray?.controls?.length ||
      (this.packagesFormArray?.controls?.length === 1 && !this.packagesFormArray?.controls?.[0].valid)
    );
  }

  public get checkIsFormEdited(): boolean {
    return this.packagesFormArray.controls.some((item: AbstractControl) => !Boolean(item.get('isView').value));
  }

  private saveQuote(): void {
    const route: RouteModel = this.possibleRoute;

    if (!route || this.isNotFoundRoutes) {
      return;
    }

    this.voyagesRepository
      .searchByRouteId(route.id, {})
      .pipe(take(1))
      .subscribe((voyages: PaginatedResultModel<VoyageModel>) => {
        const packages: PackageUpdateModel[] = this.lclContainerService.convertPackagesDataForCreateSending(
          this.packagesFormArray.value || [],
          true,
        );

        const availableVoyages: VoyageModel[] = (voyages.items || []).filter(
          (item: VoyageModel) =>
            DateHelper.isSameOrAfter(item.validityDate, DateHelper.now) &&
            ![enumVoyageStatus.EXPIRED, enumVoyageStatus.OPTIMIZED].includes(item.status),
        );

        if (!availableVoyages?.length) {
          return;
        }

        const data: QuoteUpdateModel = {
          incoTermsType: this.formGroup.get('incoTermsType').value,
          shippingRole: this.formGroup.get('shippingRole').value,
          voyageId: UtilsHelper.minElementBy<VoyageModel, string>(availableVoyages, 'estimatedDepartureTime', DateHelper.getTime)?.id,
          companyId: this.company?.id,
          packages,
        };

        data.shippingRole = [enumIncotermsType.FCA, enumIncotermsType.FOB].includes(data.incoTermsType)
          ? enumShippingRole.IMPORTER
          : enumShippingRole.SHIPPER;

        window.top.location.href = `/auth/sign-up?lclData=${JSON.stringify(data)}`;
      });
  }

  private getPossibleRoutes(fromPortId: number, toPortId: number): void {
    this.routesRepository
      .searchV2({ fromPortId, toPortId })
      .pipe(takeUntil(this.destroy$))
      .subscribe((routes: RouteModel) => {
        this.isNotFoundRoutes = false;
        this.onCloseSendNotification();

        if (!!this.fromPort && !!this.fromPort) {
          this.sendGAEvent();
        }

        // find routes where voyage validityDate is sameOrAfter now
        this.possibleRoute = routes;

        if (
          !this.possibleRoute ||
          !(this.possibleRoute?.voyages || []).some(
            (voyage: VoyageModel) =>
              DateHelper.isSameOrAfter(voyage.validityDate, DateHelper.now) &&
              ![enumVoyageStatus.EXPIRED, enumVoyageStatus.OPTIMIZED].includes(voyage.status),
          )
        ) {
          this.isNotFoundRoutes = true;
          this.isAddingPackages = true;
        }

        this.cdr.markForCheck();
      });
  }

  private sendGAEvent(): void {
    const user: UserModel = this.companiesService.getUser();

    this.ga.emitCustomEvent(
      enumGATagEventCategory.EezyFreight,
      enumGATagEventAction.EezyFreightSelectedRoute,
      `USER: ${user?.externalId}, FROM: ${this.fromPort.displayName} (${this.fromPort.unCode}), ${this.fromPort.country?.description}, TO: ${this.toPort.displayName} (${this.toPort.unCode}), ${this.toPort.country?.description}`,
    );
  }

  private calcTotals(): void {
    const values: PackageUpdateModel[] = this.packagesFormArray.getRawValue();

    this.totalValue = this.lclContainerService.totalCBM(values);
    this.totalWeight = this.lclContainerService.totalWeight(values);
    this.totalUnits = this.lclContainerService.totalUnits(values);

    this.isFormEdited = this.checkIsFormEdited;
  }

  private sendHeight(): void {
    const defaultHeight: number = 500;
    const defaultMargin: number = 80;
    window.parent.postMessage({ height: (this.elementView?.nativeElement?.offsetHeight || defaultHeight) + defaultMargin }, '*');
  }

  private setupHeightMutationObserver(): void {
    const observable$: Observable<void> = new Observable((observer: Subscriber<void>) => {
      const callback: () => void = () => {
        observer.next();
      };

      const elementObserver: MutationObserver = new MutationObserver(callback);

      const config: { attributes: boolean; childList: boolean; subtree: boolean } = {
        attributes: true,
        childList: true,
        subtree: true,
      };
      elementObserver.observe(this.elementView.nativeElement, config);
    });

    observable$.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe(() => {
      this.heightSendTrigger$.next();
    });
  }

  private markFormValidity(): void {
    this.fh.markAsTouched(this.formGroup);
    this.emailFc.markAsTouched();
    this.nameFc.markAsTouched();
    this.packagesView.forceUpdateForm();
    this.cdr.markForCheck();
  }

  private setFormErrors(data: CreateExternalManualQuoteError | CreateExistingUserManualQuoteError): void {
    if (data?.payload?.tag === enumRespErrors.CannotValidateEmail) {
      this.emailFc.setErrors({ required: true });
    }

    if (data?.payload?.tag === enumRespErrors.RecaptchaTokenInvalid) {
      this.emailFc.setErrors({ invalid: true });
    }

    if (data?.payload?.tag === enumRespErrors.AccountTemporarilyLocked) {
      this.emailFc.setErrors({ accountLocked: true });
    }

    if (data?.payload?.tag === enumRespErrors.InvalidUsernameOrPassword) {
      this.emailFc.setErrors({ invalidUsername: true });
      this.passwordFc.setErrors({ invalidPwd: true });
    }

    if (data?.payload?.tag === enumRespErrors.EmailNotConfirmed) {
      this.emailFc.setErrors({ emailNotConfirmed: true });
    }

    if (data?.payload?.tag === enumRespErrors.AccountBlocked) {
      this.emailFc.setErrors({ accountBlocked: true });
    }

    if (data?.payload?.tag === enumRespErrors.EmailNotExists) {
      this.emailFc.setErrors({ invalidEmail: true });
    }

    if (data?.payload?.tag === enumRespErrors.UserExists) {
      this.nameFc.setValidators(null);
      this.passwordFc.enable();
      this.passwordFc.setValidators([Validators.required]);
      this.isShowUserExistsNotification = true;
    }

    this.offerFormGroup.updateValueAndValidity();
  }
}
